import {
  type FC,
  useMemo,
  useRef,
  useState,
} from "react";

import { ComboboxCommand } from "@/ui/components/form/Combobox/ComboboxCommand";
import {
  COMBOBOX_DEFAULTS,
  type TComboboxOption,
  type TComboboxOptionGroup,
  type TComboboxOptions,
  type TComboboxProps,
  type TComboboxValue,
} from "@/ui/components/form/Combobox/Combobox.config";
import { ComboboxContent } from "@/ui/components/form/Combobox/ComboboxContent";
import { ComboboxEmpty } from "@/ui/components/form/Combobox/ComboboxEmpty";
import { ComboboxGroup } from "@/ui/components/form/Combobox/ComboboxGroup";
import { ComboboxInput } from "@/ui/components/form/Combobox/ComboboxInput";
import { ComboboxItem } from "@/ui/components/form/Combobox/ComboboxItem";
import { ComboboxList } from "@/ui/components/form/Combobox/ComboboxList";
import { ComboboxRoot } from "@/ui/components/form/Combobox/ComboboxRoot";
import { ComboboxTrigger } from "@/ui/components/form/Combobox/ComboboxTrigger";
import { FieldWrapper } from "@/ui/components/form/shared";

const isComboboxOptionGroup = (
  option: TComboboxOption | TComboboxOptionGroup,
): option is TComboboxOptionGroup => "options" in option;

const getComboboxOptionGroups = (
  options: TComboboxOptions,
): TComboboxOptionGroup[] => {
  const [ firstOption ] = options;

  if (firstOption && isComboboxOptionGroup(firstOption)) {
    return options as TComboboxOptionGroup[];
  }

  return [
    {
      label: "",
      options: options as TComboboxOption[],
    },
  ];
};

const getComboboxOptions = (
  optionGroups: TComboboxOptionGroup[],
): TComboboxOption[] => optionGroups.flatMap((optionGroup) => optionGroup.options);

const getComboboxSelectedValues = (
  value: TComboboxValue | undefined,
): string[] => {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [ value ] : [];
};

export const Combobox: FC<TComboboxProps> = (
  {
    className,
    asChild,
    contentClassName,
    disabled,
    emptyMessage = COMBOBOX_DEFAULTS.emptyMessage,
    error,
    id,
    inputClassName,
    isMultiSelect,
    label,
    hideLabel,
    onValueChange,
    options,
    placeholder = COMBOBOX_DEFAULTS.placeholder,
    required,
    searchPlaceholder = COMBOBOX_DEFAULTS.searchPlaceholder,
    triggerClassName,
    value,
    ...props
  },
) => {
  const [ open, setOpen ] = useState(false);
  const [ portalContainer, setPortalContainer ] = useState<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionGroups = useMemo(
    () => getComboboxOptionGroups(options),
    [ options ],
  );
  const flattenedOptions = useMemo(
    () => getComboboxOptions(optionGroups),
    [ optionGroups ],
  );
  const selectedValues = useMemo(
    () => getComboboxSelectedValues(value),
    [ value ],
  );
  const selectedLabel = useMemo(
    () => flattenedOptions
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label)
      .join(", "),
    [ flattenedOptions, selectedValues ],
  );
  const handleOptionSelect = (option: TComboboxOption) => {
    if (isMultiSelect) {
      const nextValue = selectedValues.includes(option.value)
        ? selectedValues.filter((selectedValue) => selectedValue !== option.value)
        : [ ...selectedValues, option.value ];

      onValueChange?.(nextValue);
      return;
    }

    onValueChange?.(option.value);
    setOpen(false);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setPortalContainer(triggerRef.current?.closest<HTMLElement>(
        "[data-component=\"SheetContent\"]",
      ) ?? null);
    }

    setOpen(nextOpen);
  };

  return (
    <FieldWrapper
      componentName="Combobox"
      className={className}
      disabled={disabled}
      error={error}
      hideLabel={hideLabel}
      label={label}
      required={required}
      asChild={asChild}
    >
      <ComboboxRoot
        {...props}
        open={open}
        onOpenChange={handleOpenChange}
      >
        <ComboboxTrigger
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={open && id ? `${id}-listbox` : undefined}
          aria-required={required}
          disabled={disabled}
          error={error}
          placeholder={placeholder}
          selectedLabel={selectedLabel || undefined}
          className={triggerClassName}
        />
        <ComboboxContent
          className={contentClassName}
          portalContainer={portalContainer}
        >
          <ComboboxCommand>
            <ComboboxInput
              className={inputClassName}
              placeholder={searchPlaceholder}
            />
            <ComboboxList
              id={id ? `${id}-listbox` : undefined}
              aria-multiselectable={isMultiSelect || undefined}
            >
              <ComboboxEmpty>{ emptyMessage }</ComboboxEmpty>
              { optionGroups.map((optionGroup) => (
                <ComboboxGroup
                  key={optionGroup.label}
                  heading={optionGroup.label || undefined}
                >
                  { optionGroup.options.map((option) => (
                    <ComboboxItem
                      key={option.value}
                      value={option.label}
                      disabled={option.disabled}
                      icon={option.icon}
                      isSelected={selectedValues.includes(option.value)}
                      onSelect={() => handleOptionSelect(option)}
                    >
                      { option.label }
                    </ComboboxItem>
                  )) }
                </ComboboxGroup>
              )) }
            </ComboboxList>
          </ComboboxCommand>
        </ComboboxContent>
      </ComboboxRoot>
    </FieldWrapper>
  );
};

Combobox.displayName = "Combobox";
